/**
 * MarketContextWidget - Widget 1: Market Context
 *
 * Displays market analysis data: Trend, Key Levels, Momentum
 * "Where is the market?" - Context for trading decisions
 *
 * Part of dual-widget architecture for scalable indicator display.
 */

import React, { useState, memo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import CollapsibleSection from '../CollapsibleSection';
import EMATrendTable from '../tables/EMATrendTable';
import FibonacciTable from '../tables/FibonacciTable';
import ADXTable from '../tables/ADXTable';

/**
 * MarketContextWidget Component
 */
const MarketContextWidget = memo(({
    strategyDashboard,
    currentState,
    trendScore,
    overallTrend,
    isCollapsed = false,
    onToggleCollapse,
    collapsible = true
}) => {
    // Track which sections are expanded
    const [expandedSections, setExpandedSections] = useState({
        trend: true,      // Default expanded
        levels: false,
        momentum: false
    });

    const handleSectionToggle = (sectionId, isExpanded) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: isExpanded
        }));
    };

    // Get trend color for header
    const getTrendColor = (trend) => {
        if (trend?.includes('UP')) return '#22c55e';
        if (trend?.includes('DOWN')) return '#ef4444';
        return '#fbbf24';
    };

    // Get ADX badge value
    const getAdxBadge = () => {
        const adx = strategyDashboard?.adxAnalysis?.h4?.adx;
        return adx ? `ADX ${adx.toFixed(1)}` : null;
    };

    // Get Fib badge value
    const getFibBadge = () => {
        const nearest = strategyDashboard?.fibonacciLevels?.nearestLevel;
        return nearest ? `Near ${nearest}` : null;
    };

    return (
        <Box
            sx={{
                backgroundColor: '#0f0f0f',
                border: '1px solid #374151',
                borderRadius: '4px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: isCollapsed ? 'auto' : '180px',
                maxHeight: isCollapsed ? 'auto' : 'calc(50vh - 100px)'
            }}
        >
            {/* Widget Header */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    borderBottom: '2px solid #374151',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 0.5
                }}
            >
                {/* Left: Title + Trend */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <ShowChartIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#3b82f6',
                            letterSpacing: '0.5px',
                            fontFamily: 'monospace',
                            textTransform: 'uppercase'
                        }}
                    >
                        Market Context
                    </Typography>
                    {trendScore !== undefined && (
                        <>
                            <Box sx={{ width: '1px', height: '12px', backgroundColor: '#4b5563' }} />
                            <Chip
                                label={`${trendScore}/100`}
                                size="small"
                                sx={{
                                    backgroundColor: getTrendColor(overallTrend),
                                    color: '#fff',
                                    fontSize: '9px',
                                    height: '16px',
                                    fontWeight: 700
                                }}
                            />
                        </>
                    )}
                </Box>

                {/* Right: Collapse */}
                {collapsible && (
                    <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'} arrow>
                        <IconButton
                            onClick={onToggleCollapse}
                            size="small"
                            sx={{ color: '#9ca3af', padding: '2px' }}
                        >
                            {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ExpandLessIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Widget Content */}
            {!isCollapsed && (
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {/* TREND ANALYSIS Section */}
                    <CollapsibleSection
                        title="Trend Analysis"
                        icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                        badge={trendScore ? `${trendScore}/100` : null}
                        defaultExpanded={expandedSections.trend}
                        onToggle={(expanded) => handleSectionToggle('trend', expanded)}
                    >
                        <EMATrendTable data={strategyDashboard?.trendAnalysis} currentState={currentState} />
                    </CollapsibleSection>

                    {/* KEY LEVELS Section */}
                    <CollapsibleSection
                        title="Key Levels"
                        icon={<ShowChartIcon sx={{ fontSize: 14 }} />}
                        badge={getFibBadge()}
                        defaultExpanded={expandedSections.levels}
                        onToggle={(expanded) => handleSectionToggle('levels', expanded)}
                    >
                        <FibonacciTable data={strategyDashboard?.fibonacciLevels} currentPrice={currentState?.currentPrice} />
                    </CollapsibleSection>

                    {/* MOMENTUM Section */}
                    <CollapsibleSection
                        title="Momentum"
                        icon={<TimelineIcon sx={{ fontSize: 14 }} />}
                        badge={getAdxBadge()}
                        defaultExpanded={expandedSections.momentum}
                        onToggle={(expanded) => handleSectionToggle('momentum', expanded)}
                    >
                        <ADXTable data={strategyDashboard?.adxAnalysis} currentState={currentState} />
                    </CollapsibleSection>
                </Box>
            )}
        </Box>
    );
});

MarketContextWidget.displayName = 'MarketContextWidget';

export default MarketContextWidget;
